// @packages
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-native';
import {
  Text, View
} from 'react-native';
import { FieldValues, useForm } from 'react-hook-form';

// @scripts
import Button from '../../atoms/button';
import Input from '../../atoms/input';
import useStyles from './styles';
import { completeNewPassword } from '../../../services/login-service';
import { config } from '../../../core/config';
import { useAuth } from '../../../core/context/provider';
import { routes } from '../../../core/routes/config';
import {
  AT_LEAST_1_CAPITAL_LETTER,
  AT_LEAST_1_NUMBER,
  AT_LEAST_1_SPECIAL_CHARACTER,
  AT_LEAST_8_CHARACTERS
} from '../../../utils/regex';

const ChangePasswordForm = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    control, handleSubmit, setError, watch, clearErrors
  } = useForm();
  const { onLogin, userForgotPassword } = useAuth();
  const styles = useStyles();
  const navigate = useNavigate();

  const setErrorConfirmPassword = () => {
    if (watch()?.confirmPassword !== watch()?.newPassword) {
      setError('confirmPassword', {
        type: 'custom',
        message: config.text.loginForgotPassword.errorMessageConfirmNewPassword
      });
      return true;
    }
    clearErrors('confirmPassword');
    return false;
  };

  const validateNewPassword = () => {
    if (watch()?.newPassword === watch()?.currentPassword) {
      setError('newPassword', {
        type: 'custom',
        message: config.text.loginForgotPassword.errorNewPasswordIsCurrentPassword
      });
      return true;
    }
    clearErrors('newPassword');
    return false;
  };

  const onSubmit = async (data: FieldValues) => {
    const isConfirmNewPassword = setErrorConfirmPassword();
    const isCurrentPassword = validateNewPassword();
    if (
      !AT_LEAST_1_CAPITAL_LETTER.test(watch()?.newPassword)
       || !AT_LEAST_1_NUMBER.test(watch()?.newPassword)
       || !AT_LEAST_1_SPECIAL_CHARACTER.test(watch()?.newPassword)
       || !AT_LEAST_8_CHARACTERS.test(watch()?.newPassword)
    ) return;
    if (isCurrentPassword || isConfirmNewPassword) return;
    try {
      setIsLoading(true);
      const resp = await completeNewPassword(
        userForgotPassword,
        data.currentPassword,
        data.newPassword
      );
      setIsLoading(false);
      if (!resp) {
        setError('currentPassword', {
          type: 'custom',
          message: config.text.loginForgotPassword.errorCurrentPasswordIsIncorrect
        });
        return;
      }
      navigate(routes.dashboard.path);
      onLogin(resp);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const validationConfirmNewPassword = () => {
    if (watch()?.confirmPassword.length) {
      if (watch()?.confirmPassword.length >= watch()?.newPassword.length) {
        setErrorConfirmPassword();
      }
    }
  };

  const validateRegex = (value: string) => {
    if (!value) return styles.textCaptionPassword;
    if (
      AT_LEAST_1_CAPITAL_LETTER.test(value)
          && AT_LEAST_1_NUMBER.test(value)
          && AT_LEAST_1_SPECIAL_CHARACTER.test(value)
          && AT_LEAST_8_CHARACTERS.test(value)
    ) return styles.textCaptionSuccess;
    return styles.textCaptionError;
  };

  useEffect(() => {
    validationConfirmNewPassword();
  }, [watch()?.confirmPassword, watch()?.newPassword]);

  return (
    <View style={styles.container}>
      <Text style={styles.titleText}>
        {config.text.changePassword.messageChange}
      </Text>
      <Input
        control={control}
        isRequired
        label={config.text.changePassword.currentPasswordLabel}
        leftIcon={{ name: 'lock-outline', type: 'material' }}
        name="currentPassword"
        placeholder={config.text.changePassword.currentPasswordPlaceholder}
        secureText
      />
      <Input
        control={control}
        isRequired
        label={config.text.loginForgotPassword.newPasswordLabel}
        leftIcon={{ name: 'lock-outline', type: 'material' }}
        name="newPassword"
        placeholder={config.text.login.passwordPlaceholder}
        secureText
      />
      <Input
        control={control}
        isRequired
        label={config.text.loginForgotPassword.confirmNewPassword}
        leftIcon={{ name: 'lock-outline', type: 'material' }}
        name="confirmPassword"
        placeholder={config.text.loginForgotPassword.confirmNewPassword}
        secureText
      />
      <View style={styles.caption}>
        <Text style={validateRegex(watch()?.newPassword)}>
          {config.text.loginForgotPassword.atLeastCharacters}
        </Text>
      </View>
      <Button
        color="secondary"
        isLoading={isLoading}
        onClick={handleSubmit(onSubmit)}
        title={config.text.login.button}
      />
    </View>
  );
};

export default ChangePasswordForm;
